"use client";

import {
  useRef,
  useState,
  type MouseEvent,
  type SyntheticEvent,
  type TouchEvent,
} from "react";

const SWIPE_DISTANCE = 50; // px that always counts as a swipe
const FLICK_DISTANCE = 30; // px that counts when fast enough
const FLICK_MS = 300;
const DIRECTION_LOCK = 10; // px before deciding horizontal vs vertical

/**
 * Infinite image carousel for listing cards: a clone of the last slide sits at 0 and of
 * the first at the end, so position 1 is the first real slide and the ends wrap seamlessly.
 */
export function useImageCarousel<T>(images: T[]) {
  const hasMultiple = images.length > 1;
  const slides = hasMultiple
    ? [images[images.length - 1] as T, ...images, images[0] as T]
    : images;

  const [currentIndex, setCurrentIndex] = useState(() => (hasMultiple ? 1 : 0));
  const [disableTransition, setDisableTransition] = useState(false);
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const swiping = useRef(false);
  const swiped = useRef(false);

  const stop = (e?: SyntheticEvent | null) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const prev = (e?: MouseEvent | null) => {
    stop(e);
    if (hasMultiple) setCurrentIndex((i) => i - 1);
  };

  const next = (e?: MouseEvent | null) => {
    stop(e);
    if (hasMultiple) setCurrentIndex((i) => i + 1);
  };

  /** Dots map to real slides, which start at position 1. */
  const goTo = (e: MouseEvent, index: number) => {
    stop(e);
    if (hasMultiple) setCurrentIndex(index + 1);
  };

  const jumpWithoutAnimation = (index: number) => {
    setDisableTransition(true);
    setCurrentIndex(index);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setDisableTransition(false)),
    );
  };

  /** After sliding onto a clone, jump to the real slide it copies. */
  const onTransitionEnd = () => {
    if (!hasMultiple) return;
    if (currentIndex === 0) jumpWithoutAnimation(images.length);
    if (currentIndex === slides.length - 1) jumpWithoutAnimation(1);
  };

  const onTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!hasMultiple || !touch) return;
    touchStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    swiping.current = false;
    swiped.current = false;
  };

  const onTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touchStart.current || !touch || swiping.current) return;
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    if (Math.abs(dx) > DIRECTION_LOCK && Math.abs(dx) > Math.abs(dy))
      swiping.current = true;
  };

  const onTouchEnd = (e: TouchEvent) => {
    const start = touchStart.current;
    const touch = e.changedTouches[0];
    if (!start || !touch) return;
    const dx = touch.clientX - start.x;
    const elapsed = Date.now() - start.time;
    if (
      hasMultiple &&
      (Math.abs(dx) > SWIPE_DISTANCE ||
        (Math.abs(dx) > FLICK_DISTANCE && elapsed < FLICK_MS))
    ) {
      if (dx < 0) next();
      else prev();
      swiped.current = true;
    }
    touchStart.current = null;
    swiping.current = false;
  };

  /** A tap that ended a swipe must not also open the listing. Returns true when it did. */
  const consumeSwipe = (e: MouseEvent) => {
    if (!swiped.current) return false;
    stop(e);
    swiped.current = false;
    return true;
  };

  return {
    slides,
    hasMultiple,
    currentIndex,
    /** Index of the real slide in view, for the dots. */
    currentDot: hasMultiple
      ? (currentIndex - 1 + images.length) % images.length
      : 0,
    disableTransition,
    prev,
    next,
    goTo,
    onTransitionEnd,
    touchHandlers: { onTouchStart, onTouchMove, onTouchEnd },
    consumeSwipe,
  };
}
